import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { Provider } from 'react-redux';
import store from './store';
import './App.css';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import Header from './components/Header';

import PortaleHeader from './components/ProtalHeader';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import OTPVerification from './pages/OTPVerification';
import ForgotPassword from './pages/ForgotPassword';
import ChangePassword from './pages/ChangePassword';
import AuthCallback from './pages/AuthCallback';
import PrivateRoute from './components/PrivateRoute';
import Profile from './pages/Profile';
import YourAdvicePage from './pages/YourAdvice';
import PaymentOrderConfirmationPage from './pages/PaymentOrderConfirmation';
import BloodTestSummaryPage from './pages/BloodTestSummary';
import BloodTestPrescriptionPage from './pages/BloodTestPrescription';
import Footer from './components/Footer';
import NotFound from './pages/NotFound';
import PrescriptionPage from './pages/Prescription';
import OrderHistoryPage from './pages/OrderHistory';
import OrderDetailsPage from './pages/OrderDetails';

import PersonalInformation from './pages/PersonalInformation';
import ActivateBarcode from './pages/ActivateBarcode';
import OnboardingChangePassword from './pages/OnboardingChangePassword';
import MedicalInfo from './pages/MedicalInfo';
import RegisterNewTestPage from './pages/RegisterNewTest';
import RegisterNewTestRecipientPage from './pages/RegisterNewTestRecipient';
import RegisterNewTestSuccessPage from './pages/RegisterNewTestSuccess';
import KitToewijzenPage from './pages/KitToewijzen';
import ExternalRedirect from './components/ExternalRedirect';
import { initAnalytics } from './utils/analytics';
import { useEffect } from 'react';
import MedicalQuestionaingPage from './pages/MedicalQuestionaing';
import MedicalQuestionnaireQuestionsPage from './pages/MedicalQuestionnaireQuestions';
import QuestionnaireSummaryPage from './pages/QuestionnaireSummary';


function AppLayout() {
  const location = useLocation();
  usePageTracking();

  // If Supabase OAuth is configured with a redirect URL that lands on `/` or an auth
  // page instead of `/auth/callback`, the session is never finalized and the user
  // never reaches post-login routes (e.g. `/blood-test-summary`). Forward known
  // OAuth return query/hash shapes to `/auth/callback` (common on staging).
  useEffect(() => {
    const { pathname, search, hash } = location;
    if (!['/', '/signin', '/signup'].includes(pathname)) return;
    const s = search || '';
    const h = hash || '';
    const looksLikeOAuthReturn =
      s.includes('code=') ||
      s.includes('error=') ||
      h.includes('access_token') ||
      (h.length > 1 && (h.includes('error=') || h.includes('code=')));
    if (!looksLikeOAuthReturn) return;
    window.location.replace(
      `${window.location.origin}/auth/callback${s}${h}`
    );
  }, [location.pathname, location.search, location.hash]);




  const publicRoutes = [
    '/',
    '/signin',
    '/signup',
    '/otp-verification',
    '/forgot-password',
    '/change-password',
    '/kit-toewijzen',
    '/contact',
    '*',
  ];
  const noHeaderRoutes = ['/auth/callback', '/kit-toewijzen'];
  const isPublicRoute = publicRoutes.includes(location.pathname);
  const isQuestionnaireQuestionsRoute =
    location.pathname.startsWith('/medical-questioning/questions') ||
    location.pathname.startsWith('/medical-questionaing/questions') ||
    location.pathname.startsWith('/medical-questionnaire/questions');
  const isQuestionnaireFlowRoute = isQuestionnaireQuestionsRoute;
  const isNoHeaderRoute =
    noHeaderRoutes.includes(location.pathname) ||
    isQuestionnaireQuestionsRoute;
  // Storefront paths now live in the separate website app; here they only
  // render ExternalRedirect, so skip portal chrome for them.
  const isLegacyStorefrontRoute =
    location.pathname === '/blood-test-kit' ||
    location.pathname === '/product' ||
    location.pathname.startsWith('/product/') ||
    location.pathname === '/cart' ||
    location.pathname === '/checkout-complete' ||
    location.pathname === '/about-us-page' ||
    location.pathname.startsWith('/pages/');
  const isStorefrontRoute = isLegacyStorefrontRoute;

  useEffect(() => {
    const applyBodyOverflow = () => {
      if (!isQuestionnaireFlowRoute) {
        document.body.style.overflow = '';
        return;
      }
      // Questionnaire scrolls inside MedicalQuestionnaireFlow; lock body to avoid trapped layouts.
      document.body.style.overflow = 'hidden';
    };

    applyBodyOverflow();
    window.addEventListener('resize', applyBodyOverflow);

    return () => {
      window.removeEventListener('resize', applyBodyOverflow);
      document.body.style.overflow = '';
    };
  }, [isQuestionnaireFlowRoute]);

  return (
    <>
      {isPublicRoute && !isStorefrontRoute && location.pathname !== '/kit-toewijzen' && <Header />}
      {!isPublicRoute && !isNoHeaderRoute && !isStorefrontRoute && <PortaleHeader />}
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/" element={<Navigate to="/signin" replace />} />
        {/* Legacy storefront paths: forward to the separate website app. */}
        <Route path="/blood-test-kit" element={<ExternalRedirect />} />
        <Route path="/product/:slug" element={<ExternalRedirect />} />
        <Route path="/product" element={<ExternalRedirect />} />
        <Route path="/cart" element={<ExternalRedirect />} />
        <Route path="/checkout-complete" element={<ExternalRedirect />} />
        <Route path="/pages/prik-instructies" element={<ExternalRedirect />} />
        <Route path="/pages/prik-instructies-tasso" element={<ExternalRedirect />} />
        <Route path="/about-us-page" element={<ExternalRedirect />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/otp-verification" element={<OTPVerification />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/kit-toewijzen" element={<KitToewijzenPage />} />
        <Route
          path="/contact"
          element={
            <div className="px-4 py-6 sm:px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-48 md:py-8">
              Contact Page
            </div>
          }
        />
        <Route
          path="/dashboard"
          element={
            <div className="px-4 py-6 sm:px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-48 md:py-8">
              Dashboard
            </div>
          }
        />
        <Route
          path="/your-advice"
          element={
            <PrivateRoute allowedRoles={['Patient']}>
              <YourAdvicePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/payment-order-confirmation"
          element={
            <PrivateRoute allowedRoles={['Patient', 'Supervisor']}>
              <PaymentOrderConfirmationPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/blood-test-summary"
          element={
            <PrivateRoute allowedRoles={['Patient', 'Supervisor']}>
              <BloodTestSummaryPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/register-new-test"
          element={
            <PrivateRoute allowedRoles={['Patient', 'Supervisor']}>
              <RegisterNewTestPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/register-new-test/recipient"
          element={
            <PrivateRoute allowedRoles={['Patient', 'Supervisor']}>
              <RegisterNewTestRecipientPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/register-new-test/success"
          element={
            <PrivateRoute allowedRoles={['Patient', 'Supervisor']}>
              <RegisterNewTestSuccessPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/prescription"
          element={
            <PrivateRoute allowedRoles={['Doctor/Nurse', 'Supervisor']}>
              <PrescriptionPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/blood-test-prescription"
          element={
            <PrivateRoute allowedRoles={['Supervisor']}>
                <BloodTestPrescriptionPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/personal-information"
          element={
            <PrivateRoute>
              <PersonalInformation />
            </PrivateRoute>
          }
        />
        <Route
          path="/onboarding/change-password"
          element={
            <PrivateRoute>
              <OnboardingChangePassword />
            </PrivateRoute>
          }
        />
        <Route
          path="/activate-barcode"
          element={
            <PrivateRoute>
              <ActivateBarcode />
            </PrivateRoute>
          }
        />
        {/* Temporary: show 404 if user opens "Personal Details" URL explicitly */}
        <Route path="/medical-info" element={<NotFound />} />
        <Route
          path="/medical-info"
          element={
            <PrivateRoute>
              <MedicalInfo />
            </PrivateRoute>
          }
        />
        <Route
          path="/medical-questioning"
          element={
            <PrivateRoute>
              <MedicalQuestionaingPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/medical-questionnaire"
          element={
            <PrivateRoute>
              <MedicalQuestionaingPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/medical-questioning/questions"
          element={
            <PrivateRoute>
              <MedicalQuestionnaireQuestionsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/medical-questionnaire/questions"
          element={
            <PrivateRoute>
              <MedicalQuestionnaireQuestionsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/medical-questionaing"
          element={<Navigate to="/medical-questioning" replace />}
        />
        <Route
          path="/medical-questionaing/questions"
          element={<Navigate to="/medical-questioning/questions" replace />}
        />
        <Route
          path="/medical-questionnaire-summary"
          element={
            <PrivateRoute>
              <QuestionnaireSummaryPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <div className="px-4 py-6 sm:px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-48 md:py-8">
              Settings
            </div>
          }
        />
        <Route
          path="/order-history"
          element={
            <PrivateRoute allowedRoles={['Doctor/Nurse', 'Supervisor']}>
              <OrderHistoryPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/order-details/:orderId"
          element={
            <PrivateRoute allowedRoles={['Doctor/Nurse', 'Supervisor']}>
              <OrderDetailsPage />
            </PrivateRoute>
          }
        />
      
        <Route
          path="/portal/*"
          element={
            <div className="px-4 py-6 sm:px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-48 md:py-8">
              Portal Home test
            </div>
          }
        />
        
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isStorefrontRoute && !isQuestionnaireFlowRoute && location.pathname !== '/kit-toewijzen' && <Footer />}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ top: '100px' }}
      />
    </>
  );
}

//adding page tracking//
const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    if (!window.gtag) return;

    window.gtag('config', process.env.REACT_APP_GA_ID, {
      page_path: location.pathname,
    });
  }, [location]);
};



function App() {

  //adding gtag//
  useEffect(() => {
    initAnalytics();
  }, []);


  return (
    <Provider store={store}>
      <Router>
        <AppLayout />
      </Router>
    </Provider>
  );
}

export default App;
